import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useLocalization } from "../../../hooks/useLocalization";
import { useAddWhitelistedEmails } from "./useAddWhitelistedEmails";

type EmailFormData = {
  email: string;
};

interface UseAddAndVerifyEmailsProps {
  whiteListedEmails?: string[];
  onClose: () => void;
  licenseId: string;
}

export interface UseAddAndVerifyEmailsResult {
  form: ReturnType<typeof useForm<EmailFormData>>;
  emailValue: string;
  emailError: string | undefined;
  addedEmailAddresses: string[];
  handleVerifyEmail: () => void;
  handleFormSubmit: (event: React.FormEvent) => void;
  handleCancel: () => void;
  handleRemoveEmail: (email: string) => void;
  handleSubmit: () => void;
  addMultipleEmails: (emails: string[]) => void;
  isSubmitDisabled: boolean;
  isMutationPending: boolean;
  mutationError: Error | null;
}

export const useAddAndVerifyEmails = ({
  whiteListedEmails = [],
  onClose,
  licenseId,
}: UseAddAndVerifyEmailsProps): UseAddAndVerifyEmailsResult => {
  const { t } = useLocalization();
  const [addedEmailAddresses, setAddedEmailAddresses] = useState<string[]>([]);
  const {
    mutate: addEmails,
    isPending,
    error,
    reset: resetMutation,
  } = useAddWhitelistedEmails();

  const emailSchema = z.object({
    email: z.email({
      message: t("licenses.addToWhitelistDialog.invalidEmail"),
    }),
  });

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const emailValue = form.watch("email");
  const emailError = form.formState.errors.email?.message;

  // Clear error when user changes the email input
  useEffect(() => {
    if (emailError && emailValue) {
      form.clearErrors("email");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue]);

  const handleVerifyEmail = () => {
    const trimmedEmail = emailValue.trim();

    if (addedEmailAddresses.includes(trimmedEmail)) {
      form.setError("email", {
        type: "manual",
        message: t("licenses.addToWhitelistDialog.emailAlreadyAdded"),
      });

      return;
    }

    if (whiteListedEmails.includes(trimmedEmail)) {
      form.setError("email", {
        type: "manual",
        message: t("licenses.addToWhitelistDialog.emailAlreadyWhitelisted"),
      });

      return;
    }

    setAddedEmailAddresses((prev) => [...prev, trimmedEmail]);
    form.reset();
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void form.handleSubmit(handleVerifyEmail)();
  };

  const handleCancel = () => {
    form.reset();
    resetMutation();
    setAddedEmailAddresses([]);
    onClose();
  };

  const handleRemoveEmail = (email: string) => {
    setAddedEmailAddresses((prevEmails) =>
      prevEmails.filter((e) => e !== email),
    );
  };

  const addMultipleEmails = (emails: string[]) => {
    const newEmails = emails.filter(
      (email) =>
        !addedEmailAddresses.includes(email) &&
        !whiteListedEmails.includes(email),
    );
    setAddedEmailAddresses((prev) => [...prev, ...newEmails]);
  };

  const handleSubmit = () => {
    if (addedEmailAddresses.length === 0) return;

    addEmails({
      licenseId,
      emails: addedEmailAddresses,
      onSuccess: () => {
        setAddedEmailAddresses([]);
        form.reset();
        onClose();
      },
    });
  };

  const isSubmitDisabled = addedEmailAddresses.length === 0 || isPending;

  return {
    form,
    emailValue,
    emailError,
    addedEmailAddresses,
    handleVerifyEmail,
    handleFormSubmit,
    handleCancel,
    handleRemoveEmail,
    handleSubmit,
    addMultipleEmails,
    isSubmitDisabled,
    isMutationPending: isPending,
    mutationError: error,
  };
};
